import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useDiscover } from 'src/hook/components/useDiscover';
import { useLearnWizard } from 'src/hook/components/useLearnWizard';
import { useGeneralSettings } from 'src/hook/data/useGeneralSettings';
import { useLearning } from 'src/hook/data/useLearning';
import useUser from 'src/hook/data/useUser';
import { useWordData } from 'src/hook/data/useWordData';
import usePopUp from 'src/hook/modal/usePopUp';
import { Actions, ButtonType, LearnWizardStep, PageSource } from 'src/model/enum/enums';
import { Word } from 'src/model/Word';
import styles from './ButtonSection.module.scss';

type ButtonSectionProps = {
    word: Word;
    moveNext: () => void;
}

export const ButtonSection = ({ word, moveNext }: ButtonSectionProps) => {
    const { shouldLearn, alreadeyKnew, unDecided, remembered } = useLearning();
    const { learningInfo } = useLearning();
    const [existWord, setExistWord] = useState<string>("");
    const { open } = useLearnWizard();
    const { wordData } = useWordData(word.ID);
    const [searchParams] = useSearchParams();
    const wordListId = searchParams.get('id');
    const { incrementUncoveredWordCount, generalSettings } = useGeneralSettings();
    const { showLinkedInPopUp, showRangePopUp } = usePopUp();
    const { isProUser, userInfo } = useUser();
    const { setShouldLearnCount, setAlreadyKnewCount, lastButtonType, setLastButtonType, shouldLearnCount, alreadyKnewCount } = useDiscover();
    const { t } = useTranslation();

    const checkPopUp = useCallback((buttonType: ButtonType) => {
        if (!isProUser()) {
            incrementUncoveredWordCount();
        }
        if (userInfo?.ShowLinkedin && generalSettings.UncoverWordCount === 6) {
            showLinkedInPopUp();
        }

        if (!generalSettings.ShowRangePopUp) return;

        // Handle range popup
        if (lastButtonType && lastButtonType !== buttonType) {
            setShouldLearnCount(0);
            setAlreadyKnewCount(0);
        }
        setLastButtonType(buttonType);

        if (buttonType === ButtonType.ShouldLearn) {
            const newCount = shouldLearnCount + 1;
            if (newCount >= 10) {
                showRangePopUp(false);
                setShouldLearnCount(0);
            } else {
                setShouldLearnCount(newCount);
            }
        } else if (buttonType === ButtonType.AlreadyKnew) {
            const newCount = alreadyKnewCount + 1;
            if (newCount >= 10) {
                showRangePopUp(true);
                setAlreadyKnewCount(0);
            } else {
                setAlreadyKnewCount(newCount);
            }
        }
    }, [
        isProUser, incrementUncoveredWordCount, userInfo?.ShowLinkedin,
        generalSettings, showLinkedInPopUp, lastButtonType,
        setShouldLearnCount, setAlreadyKnewCount, setLastButtonType,
        showRangePopUp, shouldLearnCount, alreadyKnewCount
    ]);

    const handleShouldLearn = useCallback(() => {
        shouldLearn(word, wordListId === "-1");
        checkPopUp(ButtonType.ShouldLearn);

        if (learningInfo?.UnknownWordIds && learningInfo?.UnknownWordIds?.length >= 5) {
            moveNext?.();
        }
    }, [word, shouldLearn, learningInfo?.UnknownWordIds, moveNext, wordListId, checkPopUp]);

    const handleAlreadyKnew = useCallback(() => {
        alreadeyKnew(word, wordListId === "-1");
        checkPopUp(ButtonType.AlreadyKnew);
        moveNext?.();
    }, [word, alreadeyKnew, moveNext, wordListId, checkPopUp]);

    const handleRemove = useCallback(() => {
        unDecided(word);
    }, [word, unDecided]);

    const handleLearnNowContinue = useCallback(async () => {
        if (existWord === Actions.Knew) {
            moveNext?.();
        } else {
            remembered(word.ID);
            open(LearnWizardStep.First, { word, wordData, pageSource: PageSource.Discover });
        }
    }, [word, open, existWord, moveNext, wordData, remembered]);

    useEffect(() => {
        if (learningInfo?.KnownWordIds.find(x => x === word.ID))
            setExistWord(Actions.Knew)
        else if (learningInfo?.UnknownWordIds.find(x => x === word.ID))
            setExistWord(Actions.Learn)
        else
            setExistWord('');
    }, [learningInfo?.KnownWordIds, learningInfo?.UnknownWordIds, word.ID]);

    if (!existWord) {
        return (
            <div className={styles.buttonSection}>
                <button onClick={() => handleShouldLearn()}>{t('Should learn')}</button>
                <button onClick={() => handleAlreadyKnew()} className='turquoiseButton'>{t('Already knew')}</button>
            </div>
        );
    }

    return (
        <section className={styles.selectSection}>
            <div className={styles.labelSection}>
                <span className={styles.label}>{t('Your knowledge:')}</span>
                <div onClick={handleRemove}
                    className={`${styles.styledButton} ${existWord === Actions.Knew ? styles.knew : styles.learn}`}>
                    {existWord === Actions.Knew ? t('Already Knew') : t('Should Learn')} ▼
                </div>
            </div>
            <button className={styles.learnNowButton} onClick={handleLearnNowContinue}>
                {existWord === Actions.Knew ? t('Continue') : t('Learn Now')}
            </button>
        </section>
    );
};
